import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedImageWidget, SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { mediaWidgetDataOfImageBlock } from 'fym-shared/src/smartlinks/mediaWidget';
import { TypedMemo } from '../../../common/types';
import { useCreateWidgetMutation, useDeleteWidgetMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { newWidgetUUID } from '../blocks/newWidgetUUID';
import { useEditorSaves } from '../saves/useEditorSaves';
import { BlockAutosave } from './useBlockAutosave';
import { formMessages } from './formMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for every block form */
import styles from './SMLBlockForm.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ImageBlockFormProps {
  widget: SerializedImageWidget;
  accountId: number;
  smartLinkId: number;
  autosave: BlockAutosave;
  /** The blocks of the page now, to find the new Media block in the answer. */
  pageWidgets: readonly SerializedWidget[];
  /** The new Media block opens in place of this one. */
  onReplaced: (widgetUUID: string | null) => void;
}

/**
 * An old Image block (design d9). It keeps working; "Turn into Media" puts a Media block with the
 * same file at the same place and deletes the Image block.
 */
const ImageBlockFormComponent: React.FC<ImageBlockFormProps> = ({
  widget,
  accountId,
  smartLinkId,
  autosave,
  pageWidgets,
  onReplaced,
}) => {
  const intl = useIntl();
  const [createWidget] = useCreateWidgetMutation();
  const [deleteWidget] = useDeleteWidgetMutation();
  const saves = useEditorSaves();
  const [isBusy, setBusy] = useState(false);
  const { markSaving, markFailed } = autosave;
  const picture = widget.data.imageProcessedUrl ?? widget.data.image?.url ?? null;

  const turnIntoMedia = useCallback(() => {
    const { image } = widget.data;
    if (image === null) {
      return;
    }
    const run = async (): Promise<boolean> => {
      setBusy(true);
      markSaving();
      const created = await createWidget({
        accountId,
        smartLinkId,
        type: SmartLinkWidgetType.media,
        data: mediaWidgetDataOfImageBlock(image.id),
        position: widget.position,
        hidden: widget.hidden === true ? true : undefined,
      });
      if (!isSuccessfulResult(created)) {
        setBusy(false);
        markFailed();
        return false;
      }
      const mediaUUID = newWidgetUUID(pageWidgets, created.data.smartLink.recentSmartLinkVersion.widgets);
      const deleted = await deleteWidget({ accountId, smartLinkId, widgetUUID: widget.uuid });
      onReplaced(mediaUUID);
      return isSuccessfulResult(deleted);
    };
    // Both calls are one save for Publish: it waits for the create and the delete.
    const done = run();
    (saves?.track(done, (ok) => !ok) ?? done).catch(() => {
      setBusy(false);
      markFailed();
    });
  }, [
    accountId,
    createWidget,
    deleteWidget,
    markFailed,
    markSaving,
    onReplaced,
    pageWidgets,
    saves,
    smartLinkId,
    widget,
  ]);

  return (
    <div className={styles.form} data-testid={`form-${SmartLinkWidgetType.image}`}>
      {picture !== null && <img src={picture} alt="" className={styles.input} />}
      {widget.data.image === null ? (
        <p className={`${styles.note} ${styles.noteWarn}`}>{intl.formatMessage(formMessages.imageNoFile)}</p>
      ) : (
        <>
          <p className={styles.note}>
            <FontAwesomeIcon icon={faWandMagicSparkles} className={styles.noteIcon} />
            {intl.formatMessage(formMessages.imageNote)}
          </p>
          <button type="button" className={styles.secondaryButton} onClick={turnIntoMedia} disabled={isBusy}>
            {intl.formatMessage(formMessages.imageTurn)}
          </button>
        </>
      )}
    </div>
  );
};

export const ImageBlockForm = TypedMemo(ImageBlockFormComponent);
